import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../utils/api";
import { BILLING_CATEGORIES } from "../../utils/jobCard";
import type { GenerateBillPayload } from "../../utils/buildGenerateBillWorkbook";

export type BillingLab = {
  token: string;
  user_name: string;
  organisation?: string | null;
  partner?: string | null;
  has_pricing?: boolean;
};

export type LabBillingPricing = {
  lab_token: string;
  lab_name: string;
  organisation: string;
  partner: string | null;
  invoice_name: string;
  currency: string;
  updated_by: string | null;
  updated_at: string | null;
  prices: Record<string, number>;
};

export type LabBillingDetailedRow = {
  lab_token: string;
  user_name: string;
  organisation: string;
  partner: string | null;
  lab_name: string;
  currency: string;
  has_pricing: boolean;
  updated_by: string | null;
  updated_at: string | null;
  prices: Record<string, number>;
};

type LabBillingPricingState = {
  labs: BillingLab[];
  detailedLabs: LabBillingDetailedRow[];
  pricing: LabBillingPricing | null;
  labsLoading: boolean;
  detailedLoading: boolean;
  pricingLoading: boolean;
  saving: boolean;
  generatingBill: boolean;
  error: string | null;
};

const emptyPrices = (): Record<string, number> => {
  const prices: Record<string, number> = {};
  for (const category of BILLING_CATEGORIES) {
    prices[category] = 0;
  }
  return prices;
};

const initialState: LabBillingPricingState = {
  labs: [],
  detailedLabs: [],
  pricing: null,
  labsLoading: false,
  detailedLoading: false,
  pricingLoading: false,
  saving: false,
  generatingBill: false,
  error: null,
};

export const fetchBillingLabs = createAsyncThunk(
  "labBillingPricing/fetchLabs",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/billing-pricing/labs", {
        withCredentials: true,
      });
      return res.data.data as BillingLab[];
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.message || "Failed to load labs"
      );
    }
  }
);

export const fetchBillingLabsDetailed = createAsyncThunk(
  "labBillingPricing/fetchLabsDetailed",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get("/api/billing-pricing/labs/detailed", {
        withCredentials: true,
      });
      return res.data.data as LabBillingDetailedRow[];
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message ||
          err.message ||
          "Failed to load lab pricing details"
      );
    }
  }
);

export const fetchLabBillingPricing = createAsyncThunk(
  "labBillingPricing/fetchPricing",
  async (labToken: string, { rejectWithValue }) => {
    try {
      const res = await api.get(
        `/api/billing-pricing/${encodeURIComponent(labToken)}`,
        { withCredentials: true }
      );
      return res.data.data as LabBillingPricing;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.message || "Failed to load pricing"
      );
    }
  }
);

export const saveLabBillingPricing = createAsyncThunk(
  "labBillingPricing/savePricing",
  async (
    {
      labToken,
      invoice_name,
      currency,
      prices,
    }: {
      labToken: string;
      invoice_name: string;
      currency: string;
      prices: Record<string, number>;
    },
    { rejectWithValue }
  ) => {
    try {
      const res = await api.put(
        `/api/billing-pricing/${encodeURIComponent(labToken)}`,
        { invoice_name, currency, prices },
        { withCredentials: true }
      );
      return res.data.data as {
        lab_token: string;
        invoice_name: string;
        currency: string;
        prices: Record<string, number>;
        updated_by: string;
        updated_at: string;
      };
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.message || "Failed to save pricing"
      );
    }
  }
);

export const generateBillByMonth = createAsyncThunk(
  "labBillingPricing/generateBill",
  async (
    { month, year }: { month: number; year: number },
    { rejectWithValue }
  ) => {
    try {
      const res = await api.post(
        "/api/billing-pricing/generate-bill",
        { month, year },
        { withCredentials: true }
      );
      return res.data.data as GenerateBillPayload;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.message || err.message || "Failed to generate bill"
      );
    }
  }
);

const labBillingPricingSlice = createSlice({
  name: "labBillingPricing",
  initialState,
  reducers: {
    clearLabBillingPricing(state) {
      state.pricing = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBillingLabs.pending, (state) => {
        state.labsLoading = true;
        state.error = null;
      })
      .addCase(fetchBillingLabs.fulfilled, (state, action) => {
        state.labsLoading = false;
        state.labs = action.payload || [];
      })
      .addCase(fetchBillingLabs.rejected, (state, action) => {
        state.labsLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(fetchBillingLabsDetailed.pending, (state) => {
        state.detailedLoading = true;
        state.error = null;
      })
      .addCase(fetchBillingLabsDetailed.fulfilled, (state, action) => {
        state.detailedLoading = false;
        state.detailedLabs = (action.payload || []).map((row) => ({
          ...row,
          prices: { ...emptyPrices(), ...(row.prices || {}) },
        }));
      })
      .addCase(fetchBillingLabsDetailed.rejected, (state, action) => {
        state.detailedLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(fetchLabBillingPricing.pending, (state) => {
        state.pricingLoading = true;
        state.error = null;
      })
      .addCase(fetchLabBillingPricing.fulfilled, (state, action) => {
        state.pricingLoading = false;
        const payload = action.payload;
        state.pricing = {
          ...payload,
          prices: { ...emptyPrices(), ...(payload.prices || {}) },
        };
      })
      .addCase(fetchLabBillingPricing.rejected, (state, action) => {
        state.pricingLoading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(saveLabBillingPricing.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(saveLabBillingPricing.fulfilled, (state, action) => {
        state.saving = false;
        if (state.pricing) {
          state.pricing = {
            ...state.pricing,
            invoice_name: action.payload.invoice_name,
            currency: action.payload.currency,
            prices: { ...emptyPrices(), ...(action.payload.prices || {}) },
            updated_by: action.payload.updated_by,
            updated_at: action.payload.updated_at,
          };
        }
        const savedToken = String(action.payload.lab_token || "")
          .trim()
          .toUpperCase();
        state.labs = state.labs.map((lab) =>
          String(lab.token || "").trim().toUpperCase() === savedToken
            ? { ...lab, has_pricing: true }
            : lab
        );
        state.detailedLabs = state.detailedLabs.map((lab) =>
          String(lab.lab_token || "").trim().toUpperCase() === savedToken
            ? {
                ...lab,
                lab_name: action.payload.invoice_name,
                currency: action.payload.currency,
                prices: { ...emptyPrices(), ...(action.payload.prices || {}) },
                has_pricing: true,
                updated_by: action.payload.updated_by,
                updated_at: action.payload.updated_at,
              }
            : lab
        );
      })
      .addCase(saveLabBillingPricing.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(generateBillByMonth.pending, (state) => {
        state.generatingBill = true;
        state.error = null;
      })
      .addCase(generateBillByMonth.fulfilled, (state) => {
        state.generatingBill = false;
      })
      .addCase(generateBillByMonth.rejected, (state, action) => {
        state.generatingBill = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearLabBillingPricing } = labBillingPricingSlice.actions;
export default labBillingPricingSlice.reducer;
